import ky, { HTTPError, type Options } from 'ky'

import { getAppConfig, getDevUser, isDevAuthMode } from '@/shared/config/runtime-config'
import { log } from '@/shared/lib/log'

import { ApiError, toApiError } from './errors'

/**
 * Cliente HTTP.
 *
 * Reglas de sesion (plan 3.5), todas verificadas en `client.test.ts`:
 *
 * - Cada request lleva el ID token de Firebase en `Authorization`.
 * - **401 -> refresh forzado y UN UNICO reintento.** Si el reintento vuelve a
 *   dar 401, `onAuthFailure()` (signOut + queryClient.clear() + redirect).
 * - **403 nunca reintenta.** Un 403 no es un token viejo, es una decision del
 *   backend; reintentar solo genera ruido en los logs de auditoria.
 * - Los errores salen normalizados como `ApiError`, nunca como `HTTPError`.
 *
 * Y **toda** petición se loguea con su `x-request-id`. El backend devuelve ese
 * id en cada respuesta y lo escribe en su propio log; loguear el mismo id de
 * este lado es lo que convierte "se rompió algo" en una traza que se puede
 * buscar. Sin eso, un error de pantalla y una traza de servidor son dos hechos
 * que nunca se encuentran.
 */

export type TokenGetter = (forceRefresh?: boolean) => Promise<string | null>

interface ApiClientDeps {
  getToken: TokenGetter
  onAuthFailure: () => void | Promise<void>
}

const noAuth: ApiClientDeps = {
  getToken: async () => null,
  onAuthFailure: () => {},
}

let deps: ApiClientDeps = noAuth

/** Lo llama `AuthProvider` al montar: la capa de red no conoce Firebase. */
export function configureApiClient(next: Partial<ApiClientDeps>): void {
  deps = { ...deps, ...next }
}

export function resetApiClient(): void {
  deps = noAuth
  instance = null
}

let instance: typeof ky | null = null

function getInstance(): typeof ky {
  if (instance) return instance

  instance = ky.create({
    prefixUrl: getAppConfig().apiBaseUrl.replace(/\/+$/, ''),
    // El reintento lo maneja `request()`, que sabe refrescar el token.
    // El de ky reintentaria a ciegas con el mismo token vencido.
    retry: 0,
    timeout: 30_000,
    throwHttpErrors: true,
    hooks: {
      beforeRequest: [
        async (request) => {
          // Modo local contra el backend real: la identidad va por header y no
          // hay token que pedir. `isDevAuthMode()` se resuelve en build.
          if (isDevAuthMode()) {
            request.headers.set('X-Dev-User', getDevUser())
            return
          }
          if (request.headers.has('Authorization')) return
          const token = await deps.getToken()
          if (token) request.headers.set('Authorization', `Bearer ${token}`)
        },
      ],
    },
  })

  return instance
}

/** `prefixUrl` de ky exige paths relativos: un `/` inicial tira TypeError. */
function normalizePath(path: string): string {
  return path.replace(/^\/+/, '')
}

function isStatus(error: unknown, status: number): boolean {
  return error instanceof HTTPError && error.response.status === status
}

async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) {
    return (await response.text()) as T
  }
  return (await response.json()) as T
}

function methodOf(options: Options): string {
  return String(options.method ?? 'get').toUpperCase()
}

/** El id que ata esta respuesta con la traza del servidor. */
function requestIdOf(error: unknown): string | null {
  return error instanceof HTTPError ? error.response.headers.get('x-request-id') : null
}

/**
 * Una línea por petición. El nivel lo decide el resultado, no quien llama: un
 * 4xx es `warn` (alguien pidió algo que no puede) y un 5xx o un error de red es
 * `error` (se rompió algo nuestro). El `debug` de las respuestas OK está ahí
 * para poder reconstruir la secuencia que llevó a la que falló.
 */
function logRequest(
  level: 'debug' | 'warn' | 'error',
  method: string,
  path: string,
  status: number,
  startedAt: number,
  requestId: string | null,
  extra: Record<string, unknown> = {},
): void {
  log[level]('http_request', {
    method,
    path,
    status,
    duration_ms: Math.round(performance.now() - startedAt),
    request_id: requestId,
    ...extra,
  })
}

function levelForStatus(status: number): 'warn' | 'error' {
  return status >= 500 || status === 0 ? 'error' : 'warn'
}

export async function request<T>(path: string, options: Options = {}): Promise<T> {
  const url = normalizePath(path)
  const method = methodOf(options)
  const startedAt = performance.now()

  try {
    const response = await getInstance()(url, options)
    logRequest(
      'debug',
      method,
      url,
      response.status,
      startedAt,
      response.headers.get('x-request-id'),
    )
    return await parse<T>(response)
  } catch (error) {
    if (!isStatus(error, 401)) {
      // 403, 404, 409, 422, 5xx y errores de red salen tal cual. Sin reintento.
      const apiError = await toApiError(error)
      logRequest(
        levelForStatus(apiError.status),
        method,
        url,
        apiError.status,
        startedAt,
        apiError.requestId,
        { code: apiError.code, message: apiError.message },
      )
      throw apiError
    }

    log.info('http_unauthorized_retry', {
      method,
      path: url,
      request_id: requestIdOf(error),
    })

    const fresh = await deps.getToken(true)
    if (!fresh) {
      log.warn('http_session_ended', { method, path: url, reason: 'sin_token_nuevo' })
      await deps.onAuthFailure()
      throw await toApiError(error)
    }

    try {
      const retried = await getInstance()(url, {
        ...options,
        headers: {
          ...(options.headers as Record<string, string>),
          Authorization: `Bearer ${fresh}`,
        },
      })
      logRequest(
        'debug',
        method,
        url,
        retried.status,
        startedAt,
        retried.headers.get('x-request-id'),
        {
          retried: true,
        },
      )
      return await parse<T>(retried)
    } catch (retryError) {
      // Segundo 401 con un token recien emitido: no es el token, es la sesion.
      const apiError = await toApiError(retryError)
      logRequest(
        levelForStatus(apiError.status),
        method,
        url,
        apiError.status,
        startedAt,
        apiError.requestId,
        {
          retried: true,
          code: apiError.code,
        },
      )
      if (apiError.status === 401) {
        log.warn('http_session_ended', { method, path: url, reason: 'segundo_401' })
        await deps.onAuthFailure()
      }
      throw apiError
    }
  }
}

type Body = Record<string, unknown> | unknown[] | undefined

export const api = {
  get: <T>(path: string, options?: Options) => request<T>(path, { ...options, method: 'get' }),
  post: <T>(path: string, json?: Body, options?: Options) =>
    request<T>(path, { ...options, method: 'post', ...(json === undefined ? {} : { json }) }),
  put: <T>(path: string, json?: Body, options?: Options) =>
    request<T>(path, { ...options, method: 'put', ...(json === undefined ? {} : { json }) }),
  patch: <T>(path: string, json?: Body, options?: Options) =>
    request<T>(path, { ...options, method: 'patch', ...(json === undefined ? {} : { json }) }),
  delete: <T>(path: string, options?: Options) =>
    request<T>(path, { ...options, method: 'delete' }),
}

export { ApiError }
